// @flow
import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { UPLOAD_SOURCES } from "@cld/upload-widget";
import t from "common/i18n";
import WithSidePanel from "root/components/WithSidePanel";
import TabPanel from "tabs/hocs/TabPanel";
import { useClearTabSelectionOnUnmount } from "tabs/common/utils";
import ServiceLoginIntro from "root/components/ServiceLoginIntro";
import TabFooter from "root/components/TabFooter";
import TabProgress from "root/components/Progress/TabProgress";
import SidePanel from "./components/SidePanel";
import Header from "./components/Header";
import ResultsGrid from "./components/ResultsGrid";
import TopBar from "./components/TopBar";
import {
	selectConfigClientId,
	selectPanelOpen,
	selectTabData,
	selectSelectedIds,
	selectCurrentContext,
	selectIsFetchingUploadData,
	selectIsWaitingForSignIn,
} from "./selectors";
import bindActions, { types as ats } from "./actions";

import "./GoogleDrive.scss";

import type { TabData } from "./data/types";
import SVG_ICONS from "~/assets/icons/icons";

type Props = {
    tabData: TabData,
    selectedIds: string[],
    isFetchingUpload: boolean,
    isWaitingForSignIn: boolean,
    driveClientId: ?string,
    actions: { [string]: Function },
    addToUploadQueue: Function,
    isSidePanelOpen: boolean,
    routes: Object[],
    currentContext: string,
    batchStartCounter: number,
};

export const GoogleDriveTab = (props: Props) => {
	const {
		selectedIds,
		isFetchingUpload,
		currentContext,
		actions,
		driveClientId,
		addToUploadQueue,
		tabData,
		isSidePanelOpen,
		isWaitingForSignIn,
	} = props;

	useEffect(() => {
		if (!tabData.isLoggedIn) {
			actions[ats.INIT_AUTH]({ clientId: driveClientId });
		}
	}, [tabData.isLoggedIn, actions, driveClientId]);

	const onClearSelectedClick = useCallback(() => {
		actions[ats.CLEAR_IMAGE_SELECTION](currentContext);
	}, [currentContext, actions]);

	const onUploadButtonClick = useCallback(() => {
		actions[ats.UPLOAD_FILES]({ addToUploadQueue });
	}, [actions, addToUploadQueue]);

	const onNoAuthActionClick = useCallback(() => {
		actions[ats.DO_LOGIN]({ clientId: driveClientId });
	}, [actions, driveClientId]);

	useClearTabSelectionOnUnmount(actions[ats.CLEAR_TAB_STATE]);

	const renderTabContent = () => (
		<>
			<WithSidePanel
				sidePanel={SidePanel}
				openSidePanel={props.isSidePanelOpen}
			>
				<div className="tab-authed">
					<Header/>
					<TopBar isSidePanelOpen={isSidePanelOpen} />
					<ResultsGrid isMultiple={tabData.isMultiple}
								 itemWidth={isSidePanelOpen ? 170 : null}/>
				</div>
			</WithSidePanel>
			{isFetchingUpload && <TabProgress/>}
		</>
	);

	const renderLoginIntro = () => (
		<>
			<ServiceLoginIntro
				key="gp_login_intro"
				title={t("google_drive.no_auth_title")}
				actionTitle={t("google_drive.no_auth_action")}
				btnIcon={SVG_ICONS.GOOGLE_DRIVE}
				btnClass="btn-drive"
				actionHandler={onNoAuthActionClick}
			/>
			{isWaitingForSignIn && <TabProgress/>}
		</>
	);

	const renderFooter = () => {
		const selectionCount = selectedIds?.length || 0;

		return (
			<TabFooter
				showSelection
				selectionCount={selectionCount}
				clearSelectionHandler={onClearSelectedClick}
				uploadButtonHandler={onUploadButtonClick}
				actionButtonActive={!!selectionCount && !isFetchingUpload}
			/>
		);
	};

	return (
		<div
			className={`content-wrap bg-source-bg bg-source-bg-c google-drive-block ${
				props.tabData.isLoggedIn ? "user-active" : ""
			}`}
		>
			<div className="holder large-thin-menu-holder relative">
				{props.tabData.isLoggedIn
					? renderTabContent()
					: renderLoginIntro()}
			</div>
			{renderFooter()}
		</div>
	);
};

export default TabPanel({
	id: UPLOAD_SOURCES.GOOGLE_DRIVE,
})(
	connect(
		createStructuredSelector({
			currentContext: selectCurrentContext,
			tabData: selectTabData,
			selectedIds: selectSelectedIds,
			driveClientId: selectConfigClientId,
			isSidePanelOpen: selectPanelOpen,
			isFetchingUpload: selectIsFetchingUploadData,
			isWaitingForSignIn: selectIsWaitingForSignIn,
		}),
		(dispatch: Function) => ({
			actions: bindActions(dispatch),
		}),
	)(GoogleDriveTab),
);
